"use client";

import type { ReactNode } from "react";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import type { ConfirmSignInOutput, ResetPasswordOutput, SignInOutput } from "aws-amplify/auth";
import { signIn as amplifySignIn, signOut as amplifySignOut, AuthError, confirmResetPassword, confirmSignIn, getCurrentUser, resetPassword, updatePassword } from "aws-amplify/auth";
import { signIn as nextSignIn, signOut as nextSignOut, useSession } from "@atalan/auth/react";
import type { AppAbility } from "@atalan/authorization";
import { createAbility, interpolate } from "@atalan/authorization";
import { useAuthContext } from "@/components/AuthContext/AuthContext";
import { AuthFlowPaths } from "@/constants/Constants";
import { api } from "@/lib/api";
interface TProps {
  /**
   * Components to render inside the context provider.
   */
  children?: ReactNode;
}
type ClientMetaData = Record<string, string> | undefined;
interface IUserContext {
  token?: string | null;
  confirmationUsername?: string | null;
  userAbility?: AppAbility | null;
  profileImage?: string | null;
  signIn: (username: string, password: string, metadata: ClientMetaData) => Promise<SignInOutput | null>;
  completeNewPassword: (username: string, temporaryPassword: string, newPassword: string) => Promise<ConfirmSignInOutput | null>;
  signOut: () => Promise<void>;
  forgotPassword: (username: string) => Promise<ResetPasswordOutput | null>;
  forgotPasswordSubmit: (username: string, code: string, newPassword: string) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<boolean>;
  getOTPCode: (username: string, password: string, metadata?: ClientMetaData) => Promise<SignInOutput | null>;
  tempPassword?: string;
  storeTempPassword: (password: string) => void;
  clearTempPassword: () => void;
  confirmOtpCode: ({
    challengeResponse,
    clientMetadata
  }: {
    challengeResponse: string;
    clientMetadata: ClientMetaData;
    redirectTo?: string;
  }) => Promise<ConfirmSignInOutput | null>;
}
const defaultState: IUserContext = {
  token: null,
  confirmationUsername: null,
  userAbility: null,
  profileImage: "",
  signIn: () => Promise.resolve(null),
  completeNewPassword: () => Promise.resolve(null),
  signOut: () => Promise.resolve(),
  forgotPassword: () => Promise.resolve(null),
  forgotPasswordSubmit: () => Promise.resolve(),
  changePassword: () => Promise.resolve(false),
  getOTPCode: () => Promise.resolve(null),
  tempPassword: "",
  storeTempPassword: () => null,
  clearTempPassword: () => null,
  confirmOtpCode: () => Promise.resolve(null)
};
const Context = React.createContext<IUserContext>(defaultState);
export default function UserContext({
  children
}: TProps) {
  const {
    activeForm
  } = useAuthContext();
  const [confirmationUsername, setConfirmationUsername] = useState<string>();
  const [tempPassword, setTempPassword] = useState<string>();
  const [userAbility, setUserAbility] = useState<AppAbility | null>();
  const {
    data: session,
    status
  } = useSession();
  const router = useRouter();
  const user = session?.user;
  const {
    data: permissions
  } = api.users.getPermissions.useQuery(undefined, {
    enabled: status === "authenticated",
    cacheTime: 3600000,
    staleTime: 3600000
  });
  const profileImage = React.useMemo(() => {
    if (!user?.avatar_key) return "";
    if (user.avatar_key?.includes("app/public")) {
      return `/api/files/${user?.avatar_key}?v=${Math.floor(Math.random() * 500)}`;
    }
    return user.avatar_key;
  }, [user]);
  useEffect(() => {
    const checkSession = async () => {
      try {
        // check if user is logged in with amplify
        const {
          userId
        } = await getCurrentUser();
        if (!userId) return;

        // if user is logged in with amplify, sign out
        await amplifySignOut();
        await nextSignOut();
        setUserAbility(null);
      } catch (err) {
        if (err instanceof AuthError) {
          console.log(err.message);
          return;
        }
        console.log(err);
      }
    };

    // if session expires
    if (status === "unauthenticated") {
      checkSession();
      if (activeForm !== null && activeForm !== "FORGOT" && activeForm !== "FORGOT_SUCCESS") {
        router.push(AuthFlowPaths.LOGIN);
      }
    }
  }, [status]);
  const signOut = useCallback(async () => {
    await Promise.all([amplifySignOut(), nextSignOut()]);
  }, []);
  useEffect(() => {
    if (session?.error === "Invalid access token") {
      signOut();
    }
  }, [session, signOut]);
  useEffect(() => {
    if (permissions && !userAbility) {
      const p = JSON.stringify(permissions);
      const userPermissions = interpolate(p, {
        user: session?.user
      });
      setUserAbility(createAbility(userPermissions));
    }
  }, [permissions, session?.user, userAbility]);
  const signIn = async (username: string, password: string, metadata?: ClientMetaData) => {
    try {
      const signInOutput = await amplifySignIn({
        username,
        password,
        options: {
          authFlowType: "CUSTOM_WITH_SRP",
          clientMetadata: metadata
        }
      });
      return signInOutput;
    } catch (e) {
      const error = (e as {
        code: string;
      });
      console.log(error);
      return null;
    }
  };
  const completeNewPassword = async (username: string, tempPassword: string, newPassword: string) => {
    await amplifySignIn({
      username,
      password: tempPassword
    });
    const confirmation = await confirmSignIn({
      challengeResponse: newPassword
    });
    if (confirmation?.isSignedIn) {
      // SignOut after complete new password
      await amplifySignOut();
    }
    return confirmation;
  };
  const forgotPassword = async (username: string) => {
    const resetOutput = await resetPassword({
      username
    });
    setConfirmationUsername(username);
    return resetOutput;
  };
  const forgotPasswordSubmit = async (username: string, confirmationCode: string, newPassword: string) => {
    try {
      await confirmResetPassword({
        username,
        confirmationCode,
        newPassword
      });
    } catch (error) {
      if (error instanceof Error) {
        console.log(error.message);
      }
    }
  };
  const changePassword = async (oldPassword: string, newPassword: string) => {
    try {
      await updatePassword({
        oldPassword,
        newPassword
      });
      return true;
    } catch (error) {
      if (error instanceof Error) {
        console.log(error.message);
      }
      return false;
    }
  };
  const getOTPCode = async (username: string, password: string, metadata: ClientMetaData) => {
    const cognitoUser = await amplifySignIn({
      username,
      password,
      options: {
        authFlowType: "CUSTOM_WITH_SRP",
        clientMetadata: metadata
      }
    });
    if (cognitoUser?.nextStep.signInStep !== "DONE") {
      setConfirmationUsername(username);
    }
    return cognitoUser;
  };
  const storeTempPassword = (password: string) => setTempPassword(password);
  const clearTempPassword = () => setTempPassword("");
  const confirmOtpCode = async ({
    challengeResponse,
    clientMetadata,
    redirectTo
  }: {
    challengeResponse: string;
    clientMetadata: ClientMetaData;
    redirectTo?: string;
  }) => {
    const confirmation = await confirmSignIn({
      challengeResponse,
      options: {
        serviceOptions: {
          clientMetadata
        }
      }
    });
    if (confirmation?.nextStep.signInStep === "DONE") {
      clearTempPassword();
      // TODO: once the use is sign in, track device.
      await nextSignIn("amplify", {
        redirectTo: "/dashboard",
        username: confirmationUsername
      });
      if (redirectTo) window.location.href = redirectTo;
    }
    return confirmation;
  };
  return <Context.Provider value={{
    confirmationUsername,
    userAbility,
    profileImage,
    signIn,
    completeNewPassword,
    signOut,
    forgotPassword,
    forgotPasswordSubmit,
    changePassword,
    getOTPCode,
    tempPassword,
    storeTempPassword,
    clearTempPassword,
    confirmOtpCode
  }}>
      {children}
    </Context.Provider>;
}
export const useUserContext = () => useContext(Context);